import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const ACCOUNT_TYPES = [
  { key: 'email',        label: 'Email',         icon: '📧' },
  { key: 'social_media', label: 'Social Media',  icon: '📱' },
  { key: 'financial',    label: 'Financial',     icon: '💳' },
  { key: 'streaming',    label: 'Streaming',     icon: '🎬' },
  { key: 'shopping',     label: 'Shopping',      icon: '🛒' },
  { key: 'subscription', label: 'Subscription',  icon: '🔄' },
  { key: 'other',        label: 'Other',         icon: '💻' },
];

const WHAT_TO_DO_OPTIONS = ['Close the account', 'Transfer to spouse', 'Transfer to family member', 'Keep active', 'Download data first, then close', 'Other'];

const EMPTY_FORM = { account_type: 'email', platform_name: '', username: '', password_hint: '', url: '', what_to_do: '', notes: '' };

export default function Digital({ session }) {
  const [vault, setVault] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [session]);

  const load = async () => {
    const { data: vaultData } = await supabase.from('vaults').select('id').eq('owner_id', session.user.id).single();
    setVault(vaultData);
    if (vaultData) {
      const { data } = await supabase.from('digital_accounts').select('*').eq('vault_id', vaultData.id).order('account_type').order('platform_name');
      setAccounts(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    if (editing) {
      const { error: err } = await supabase.from('digital_accounts').update({ ...form, updated_at: new Date().toISOString() }).eq('id', editing.id);
      if (err) { setError(err.message); setSaving(false); return; }
      setSuccess(`${form.platform_name} updated.`);
    } else {
      const { error: err } = await supabase.from('digital_accounts').insert({ ...form, vault_id: vault.id, owner_id: session.user.id });
      if (err) { setError(err.message); setSaving(false); return; }
      setSuccess(`${form.platform_name} added.`);
    }
    setSaving(false); setShowForm(false); setEditing(null); setForm(EMPTY_FORM);
    setTimeout(() => setSuccess(''), 3000); load();
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({ account_type: item.account_type, platform_name: item.platform_name, username: item.username || '', password_hint: item.password_hint || '', url: item.url || '', what_to_do: item.what_to_do || '', notes: item.notes || '' });
    setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove ${item.platform_name}?`)) return;
    setDeleting(item.id);
    await supabase.from('digital_accounts').delete().eq('id', item.id);
    setDeleting(null); load();
  };

  const handleCancel = () => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setError(''); };

  const style = `
    .pg { padding: 32px; max-width: 860px; margin: 0 auto; }
    .pg-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 16px; }
    .pg-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 32px; color: #1E1040; line-height: 1.05; }
    .pg-title em { font-style: italic; color: #8B5CF6; }
    .pg-sub { font-size: 14px; color: #6B7280; margin-top: 6px; line-height: 1.5; }
    .pg-add-btn { display: flex; align-items: center; gap: 8px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; border: none; border-radius: 100px; padding: 12px 24px; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; cursor: pointer; white-space: nowrap; box-shadow: 0 4px 12px rgba(139,92,246,0.25); transition: opacity 0.2s, transform 0.2s; }
    .pg-add-btn:hover { opacity: 0.88; transform: translateY(-1px); }
    .pg-error { background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.2); border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #DC2626; margin-bottom: 16px; }
    .pg-success { background: rgba(34,197,94,0.06); border: 1px solid rgba(34,197,94,0.2); border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #16A34A; margin-bottom: 16px; }
    .pg-form { background: #fff; border: 1.5px solid #EAE5F5; border-radius: 20px; padding: 28px; margin-bottom: 28px; box-shadow: 0 4px 20px rgba(139,92,246,0.07); }
    .pg-form-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 20px; color: #1E1040; margin-bottom: 20px; }
    .pg-type-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
    .pg-type-btn { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 100px; border: 1.5px solid #EAE5F5; background: #FDFCFF; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 500; color: #6B7280; cursor: pointer; transition: all 0.15s; }
    .pg-type-btn:hover { border-color: rgba(139,92,246,0.3); color: #7C3AED; }
    .pg-type-btn.selected { border-color: #8B5CF6; background: rgba(139,92,246,0.06); color: #7C3AED; font-weight: 700; }
    .pg-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
    @media(max-width:600px){ .pg-form-grid { grid-template-columns: 1fr; } }
    .pg-field { display: flex; flex-direction: column; gap: 6px; }
    .pg-field.full { grid-column: 1 / -1; }
    .pg-label { font-size: 12px; font-weight: 700; color: #374151; }
    .pg-hint { font-size: 11px; color: #9CA3AF; }
    .pg-input, .pg-textarea, .pg-select { padding: 11px 14px; border: 1.5px solid #E5E7EB; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #1E1040; background: #FAFAFA; outline: none; transition: border-color 0.2s; width: 100%; }
    .pg-input:focus, .pg-textarea:focus, .pg-select:focus { border-color: #8B5CF6; background: #fff; }
    .pg-textarea { resize: vertical; min-height: 80px; }
    .pg-form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 4px; }
    .pg-btn-cancel { padding: 10px 20px; border: 1.5px solid #EDE9E3; border-radius: 100px; background: transparent; color: #6B7280; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; }
    .pg-btn-save { padding: 10px 24px; border: none; border-radius: 100px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; box-shadow: 0 4px 12px rgba(139,92,246,0.25); transition: opacity 0.2s; }
    .pg-btn-save:hover { opacity: 0.88; }
    .pg-btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
    .pg-group { margin-bottom: 28px; }
    .pg-group-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #EAE5F5; }
    .pg-group-icon { font-size: 18px; }
    .pg-group-label { font-size: 13px; font-weight: 700; color: #374151; letter-spacing: 0.04em; text-transform: uppercase; }
    .pg-group-count { font-size: 12px; color: #9CA3AF; margin-left: auto; }
    .pg-card { background: linear-gradient(145deg, #FDFCFF, #FAF8FF); border: 1.5px solid #EAE5F5; border-radius: 14px; padding: 20px; margin-bottom: 10px; display: flex; align-items: flex-start; gap: 16px; transition: border-color 0.2s, box-shadow 0.2s; }
    .pg-card:hover { border-color: rgba(139,92,246,0.25); box-shadow: 0 4px 16px rgba(139,92,246,0.08); }
    .pg-card-icon { width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0; background: linear-gradient(135deg, #8B5CF6, #38BDF8); display: flex; align-items: center; justify-content: center; font-size: 20px; }
    .pg-card-info { flex: 1; min-width: 0; }
    .pg-card-name { font-size: 16px; font-weight: 700; color: #1E1040; margin-bottom: 2px; }
    .pg-card-sub { font-size: 13px; color: #6B7280; margin-bottom: 8px; }
    .pg-card-details { display: flex; flex-wrap: wrap; gap: 16px; }
    .pg-card-detail { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #374151; }
    .pg-card-detail-icon { font-size: 14px; color: #8B5CF6; }
    .pg-card-action-tag { display: inline-flex; align-items: center; gap: 4px; background: rgba(139,92,246,0.08); border: 1px solid rgba(139,92,246,0.15); border-radius: 100px; padding: 3px 10px; font-size: 12px; color: #7C3AED; font-weight: 600; margin-top: 8px; }
    .pg-card-notes { margin-top: 10px; padding-top: 10px; border-top: 1px solid #EAE5F5; font-size: 13px; color: #6B7280; line-height: 1.5; }
    .pg-card-actions { display: flex; flex-direction: column; gap: 6px; flex-shrink: 0; }
    .pg-btn-edit { padding: 6px 14px; border-radius: 100px; border: 1.5px solid #EAE5F5; background: transparent; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 600; color: #6B7280; cursor: pointer; transition: all 0.15s; }
    .pg-btn-edit:hover { border-color: #8B5CF6; color: #7C3AED; }
    .pg-btn-delete { padding: 6px 14px; border-radius: 100px; border: 1.5px solid transparent; background: transparent; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 600; color: #9CA3AF; cursor: pointer; transition: all 0.15s; }
    .pg-btn-delete:hover { border-color: rgba(239,68,68,0.2); color: #EF4444; background: rgba(239,68,68,0.04); }
    .pg-empty { background: #fff; border: 2px dashed #EAE5F5; border-radius: 20px; padding: 60px 40px; text-align: center; }
    .pg-empty-icon { font-size: 48px; margin-bottom: 16px; }
    .pg-empty-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 22px; color: #1E1040; margin-bottom: 8px; }
    .pg-empty-sub { font-size: 14px; color: #6B7280; line-height: 1.6; max-width: 400px; margin: 0 auto 24px; }
    .pg-why { background: linear-gradient(135deg, rgba(139,92,246,0.05), rgba(56,189,248,0.03)); border: 1px solid rgba(139,92,246,0.12); border-radius: 16px; padding: 20px 24px; margin-bottom: 28px; display: flex; gap: 14px; align-items: flex-start; }
    .pg-why-icon { font-size: 24px; flex-shrink: 0; }
    .pg-why-text { font-size: 14px; color: #374151; line-height: 1.6; }
    .pg-why-text strong { color: #1E1040; }
    .pg-privacy-note { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #9CA3AF; margin-top: 8px; padding: 8px 12px; background: rgba(139,92,246,0.04); border-radius: 8px; }
  `;

  if (loading) return <div style={{padding: 32, color: '#6B7280', fontSize: 14}}>Loading...</div>;

  return (
    <>
      <style>{style}</style>
      <div className="pg">
        <div className="pg-header">
          <div>
            <h1 className="pg-title">Digital <em>Life</em></h1>
            <p className="pg-sub">Online accounts and subscriptions your family will need to manage or close.</p>
          </div>
          {!showForm && <button className="pg-add-btn" onClick={() => setShowForm(true)}>+ Add Account</button>}
        </div>

        {accounts.length === 0 && !showForm && (
          <div className="pg-why">
            <div className="pg-why-icon">💡</div>
            <div className="pg-why-text"><strong>Why this matters:</strong> The average person has over 100 online accounts. Without this list, your family will spend months trying to cancel subscriptions, access email, and manage your digital presence.</div>
          </div>
        )}

        {error && <div className="pg-error">{error}</div>}
        {success && <div className="pg-success">✓ {success}</div>}

        {showForm && (
          <form className="pg-form" onSubmit={handleSubmit}>
            <div className="pg-form-title">{editing ? 'Edit Account' : 'Add a Digital Account'}</div>
            <div className="pg-type-grid">
              {ACCOUNT_TYPES.map(t => (
                <button key={t.key} type="button" className={`pg-type-btn${form.account_type === t.key ? ' selected' : ''}`} onClick={() => setForm({...form, account_type: t.key})}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
            <div className="pg-form-grid">
              <div className="pg-field">
                <label className="pg-label">Platform / Service *</label>
                <input className="pg-input" type="text" placeholder="Gmail, Netflix, Amazon..." value={form.platform_name} onChange={e => setForm({...form, platform_name: e.target.value})} required />
              </div>
              <div className="pg-field">
                <label className="pg-label">Username / Email</label>
                <input className="pg-input" type="text" placeholder="john@gmail.com" value={form.username} onChange={e => setForm({...form, username: e.target.value})} />
              </div>
              <div className="pg-field">
                <label className="pg-label">Password Hint</label>
                <div className="pg-hint">A hint only — never store the actual password</div>
                <input className="pg-input" type="text" placeholder="Same as banking password" value={form.password_hint} onChange={e => setForm({...form, password_hint: e.target.value})} />
              </div>
              <div className="pg-field">
                <label className="pg-label">Website URL</label>
                <input className="pg-input" type="text" placeholder="gmail.com" value={form.url} onChange={e => setForm({...form, url: e.target.value})} />
              </div>
              <div className="pg-field full">
                <label className="pg-label">What should my family do with this account?</label>
                <select className="pg-select" value={form.what_to_do} onChange={e => setForm({...form, what_to_do: e.target.value})}>
                  <option value="">Select an action...</option>
                  {WHAT_TO_DO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="pg-field full">
                <label className="pg-label">Notes</label>
                <textarea className="pg-textarea" placeholder="Any additional context, linked accounts, important data to save..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </div>
            <div className="pg-privacy-note">🔒 Store only hints, never actual passwords. Your family needs enough information to contact the platform, not to log in directly.</div>
            <div className="pg-form-actions">
              <button type="button" className="pg-btn-cancel" onClick={handleCancel}>Cancel</button>
              <button type="submit" className="pg-btn-save" disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Account'}</button>
            </div>
          </form>
        )}

        {accounts.length === 0 && !showForm ? (
          <div className="pg-empty">
            <div className="pg-empty-icon">💻</div>
            <div className="pg-empty-title">No digital accounts yet</div>
            <p className="pg-empty-sub">Add your email accounts, social media, streaming services, and subscriptions.</p>
            <button className="pg-add-btn" onClick={() => setShowForm(true)}>+ Add Your First Account</button>
          </div>
        ) : (
          ACCOUNT_TYPES.map(type => {
            const items = accounts.filter(a => a.account_type === type.key);
            if (items.length === 0) return null;
            return (
              <div key={type.key} className="pg-group">
                <div className="pg-group-header">
                  <span className="pg-group-icon">{type.icon}</span>
                  <span className="pg-group-label">{type.label}</span>
                  <span className="pg-group-count">{items.length}</span>
                </div>
                {items.map(item => (
                  <div key={item.id} className="pg-card">
                    <div className="pg-card-icon">{type.icon}</div>
                    <div className="pg-card-info">
                      <div className="pg-card-name">{item.platform_name}</div>
                      {item.username && <div className="pg-card-sub">{item.username}</div>}
                      <div className="pg-card-details">
                        {item.url && <div className="pg-card-detail"><span className="pg-card-detail-icon">🌐</span>{item.url}</div>}
                        {item.password_hint && <div className="pg-card-detail"><span className="pg-card-detail-icon">🔑</span>Hint: {item.password_hint}</div>}
                      </div>
                      {item.what_to_do && <div className="pg-card-action-tag">→ {item.what_to_do}</div>}
                      {item.notes && <div className="pg-card-notes">{item.notes}</div>}
                    </div>
                    <div className="pg-card-actions">
                      <button className="pg-btn-edit" onClick={() => handleEdit(item)}>Edit</button>
                      <button className="pg-btn-delete" disabled={deleting === item.id} onClick={() => handleDelete(item)}>{deleting === item.id ? '...' : 'Remove'}</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
